import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { AlertService } from './alert.service';
import { AlertPriority, normalizeAlertPriority } from '../shared/const/alert-priority';

export interface PriorityColor {
  priority: AlertPriority;
  color: string;
  /** The text drawn on `color` — the alert card is filled outright, so they travel together. */
  textColor: string;
  isCustom: boolean;
}

/**
 * Applies the client's chosen priority colours to the running app.
 *
 * The queue's alert cards and the Cameras drawer's priority control read their colours
 * through `--sw-priority-<level>-*` custom properties, each declared in SCSS with the
 * built-in shade as its fallback. This service sets those properties on the document
 * root, so one fetch retints every consumer without any component subscribing to
 * anything.
 *
 * Only CUSTOMISED levels get properties set. A level nobody has changed is left to its
 * SCSS fallback, which is the hand-tuned default trio — overriding it with values derived
 * from a single ink would quietly change the look of an install that never asked for a
 * different palette.
 */
@Injectable({ providedIn: 'root' })
export class PriorityThemeService {
  private alertService = inject(AlertService);

  /** Last known palette, for the settings screen to render without re-fetching. */
  readonly colors$ = new BehaviorSubject<PriorityColor[]>([]);

  private applied = new Set<AlertPriority>();

  /**
   * Called once at app start. Failure is deliberately silent: the entire feature degrades
   * to the built-in palette, and an operator does not need a toast about band shades.
   */
  load(): void {
    this.alertService.getAlertPriorityColors().subscribe({
      next: (res: any) => {
        const colors: PriorityColor[] = (res?.data || []).map((row: any) => ({
          priority: normalizeAlertPriority(row?.priority),
          color: String(row?.color || ''),
          textColor: String(row?.text_color || ''),
          isCustom: !!row?.isCustom,
        }));
        this.colors$.next(colors);
        colors.forEach((row) => this.apply(row));
      },
      error: () => {
        // Keep the built-in palette.
      },
    });
  }

  /** Retint one level immediately, so the settings screen previews live. */
  apply(row: PriorityColor): void {
    const root = document.documentElement;
    const level = normalizeAlertPriority(row.priority);
    const prefix = `--sw-priority-${level}`;

    if (!row.isCustom) {
      this.clear(level);
      return;
    }

    const rgb = this.toRgb(row.color);
    if (!rgb) {
      this.clear(level);
      return;
    }

    // Ink is the chosen colour; the region and its border are the same hue at low alpha.
    // Alpha rather than a mixed hex so the wash keeps working over any ground.
    root.style.setProperty(`${prefix}-ink`, row.color);
    root.style.setProperty(`${prefix}-wash`, `rgba(${rgb}, 0.10)`);
    root.style.setProperty(`${prefix}-edge`, `rgba(${rgb}, 0.32)`);
    // The drawer's segmented control fills its active tile, so it needs a lighter step
    // than the wash to keep dark text on it legible.
    root.style.setProperty(`${prefix}-fill`, `rgba(${rgb}, 0.16)`);
    // Only when the client actually chose one. An empty value would otherwise resolve to
    // an invalid property and drop the card's text back to the inherited zinc grey, which
    // on a saturated fill is the one combination that cannot be read.
    if (/^#[0-9a-f]{6}$/i.test(String(row.textColor || '').trim())) {
      root.style.setProperty(`${prefix}-text`, row.textColor);
    } else {
      root.style.removeProperty(`${prefix}-text`);
    }
    this.applied.add(level);
  }

  /** Drop the overrides for one level, handing it back to the SCSS fallback. */
  clear(level: AlertPriority): void {
    const root = document.documentElement;
    const prefix = `--sw-priority-${level}`;
    ['ink', 'wash', 'edge', 'fill', 'text'].forEach((part) => {
      root.style.removeProperty(`${prefix}-${part}`);
    });
    this.applied.delete(level);
  }

  /** "#RRGGBB" -> "r, g, b". Null for anything that is not a six-digit hex. */
  private toRgb(hex: string): string | null {
    const match = /^#([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!match) {
      return null;
    }
    const value = parseInt(match[1], 16);
    return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
  }
}
